import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getLogs } from "../../api/services/logsService";
import {
    formatLogDate,
    LOG_TABLE_COLUMNS,
    LOG_TYPE_OPTIONS,
    PAGE_LIMIT_OPTIONS,
    renderBracketHighlightedText,
} from "../../utils/logUtils";
import { PAGINATION } from "../../utils/constants";
import GuestSelect from "../../components/Guest/GuestSelect";

const EMPTY_FILTERS = {
    accountName: "",
    characterName: "",
    dateFrom: "",
    dateTo: "",
};

const LogTypeBadge = ({ type }) => {
    if (type === "report") {
        return (
            <span className="admin-logs__badge admin-logs__badge--report">
                Жалоба
            </span>
        );
    }
    if (type === "ask") {
        return (
            <span className="admin-logs__badge admin-logs__badge--ask">
                Вопрос
            </span>
        );
    }
    return "—";
};

const AdminLogsPage = () => {
    const [logType, setLogType] = useState(1);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
    const [pageLimit, setPageLimit] = useState(PAGINATION.DEFAULT_LIMIT);
    const [page, setPage] = useState(1);
    const [pageInput, setPageInput] = useState("1");
    const [items, setItems] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const loadLogs = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const result = await getLogs({
                logType,
                page,
                pageLimit,
                accountName: appliedFilters.accountName,
                characterName: appliedFilters.characterName,
                dateFrom: appliedFilters.dateFrom || undefined,
                dateTo: appliedFilters.dateTo || undefined,
            });
            setItems(result.items);
            setTotalPages(result.totalPages);
            setTotalItems(result.totalItems);
            setPage(result.currentPage);
            setPageInput(String(result.currentPage));
        } catch (err) {
            setError(err.message || "Не удалось загрузить логи");
            setItems([]);
            setTotalPages(1);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [logType, page, pageLimit, appliedFilters]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    const handleLogTypeChange = (nextType) => {
        setLogType(nextType);
        setPage(1);
        setPageInput("1");
        setFilters(EMPTY_FILTERS);
        setAppliedFilters(EMPTY_FILTERS);
    };

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = (event) => {
        event.preventDefault();
        setAppliedFilters({ ...filters });
        setPage(1);
        setPageInput("1");
    };

    const handleResetFilters = () => {
        setFilters(EMPTY_FILTERS);
        setAppliedFilters(EMPTY_FILTERS);
        setPage(1);
        setPageInput("1");
    };

    const handlePageLimitChange = (event) => {
        setPageLimit(Number(event.target.value));
        setPage(1);
        setPageInput("1");
    };

    const goToPage = (nextPage) => {
        const parsed = Number(nextPage);
        if (!Number.isFinite(parsed)) {
            setPageInput(String(page));
            return;
        }
        const clamped = Math.min(Math.max(1, Math.trunc(parsed)), totalPages);
        setPage(clamped);
        setPageInput(String(clamped));
    };

    const handlePageInputChange = (event) => {
        setPageInput(event.target.value);
    };

    const handlePageInputSubmit = (event) => {
        event.preventDefault();
        goToPage(pageInput);
    };

    const showLoginFilter = logType === 1 || logType === 3;
    const columns = LOG_TABLE_COLUMNS[logType];

    const colClass = (width) =>
        width === "default"
            ? ""
            : `admin-logs__col admin-logs__col--${width}`;

    const renderCell = (item, column) => {
        switch (column.id) {
            case "accountId":
                return item.accountId ?? "—";
            case "destinationCharID":
                return item.destinationCharID ?? "—";
            case "login":
            case "playerLogin":
                return item[column.id] || "—";
            case "characterName":
            case "targetCharacterName":
                return item[column.id] || "—";
            case "oldValue":
            case "newValue":
            case "amount":
                return item[column.id] ?? "—";
            case "reason":
                return item.reason || "—";
            case "date":
                return formatLogDate(item.date);
            case "text":
                return logType === 1
                    ? renderBracketHighlightedText(item.text)
                    : item.text || "—";
            case "type":
                return <LogTypeBadge type={item.type} />;
            default:
                return "—";
        }
    };

    const renderRow = (item, index) => {
        const key = `${logType}-${item.date}-${index}`;

        return (
            <tr key={key} className="admin-logs__row">
                {columns.map((column) => (
                    <td
                        key={column.id}
                        className={colClass(column.width)}
                    >
                        {renderCell(item, column)}
                    </td>
                ))}
            </tr>
        );
    };

    const pageNumbers = useMemo(() => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, page - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        start = Math.max(1, end - maxVisible + 1);

        for (let i = start; i <= end; i += 1) {
            pages.push(i);
        }
        return pages;
    }, [page, totalPages]);

    return (
        <div className="admin-page admin-logs">
            <h1 className="acp-title admin-logs__title">Логи</h1>

            <div className="admin-logs__tabs" role="tablist">
                {LOG_TYPE_OPTIONS.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        role="tab"
                        aria-selected={logType === option.value}
                        className={`tab-button ${logType === option.value ? "tab-button--active" : ""}`}
                        onClick={() => handleLogTypeChange(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            <form className="admin-logs__filters" onSubmit={handleApplyFilters}>
                <div className="admin-logs__filters-fields">
                    <div className="admin-logs__filter">
                        <span className="admin-logs__filter-label">На странице</span>
                        <GuestSelect
                            name="pageLimit"
                            value={pageLimit}
                            onChange={handlePageLimitChange}
                            options={PAGE_LIMIT_OPTIONS}
                        />
                    </div>

                    {showLoginFilter && (
                        <label className="admin-logs__filter">
                            <span>Логин</span>
                            <input
                                type="text"
                                name="accountName"
                                className="admin-logs__input"
                                value={filters.accountName}
                                onChange={handleFilterChange}
                                placeholder="Логин аккаунта"
                                autoComplete="off"
                            />
                        </label>
                    )}

                    <label className="admin-logs__filter">
                        <span>Персонаж</span>
                        <input
                            type="text"
                            name="characterName"
                            className="admin-logs__input"
                            value={filters.characterName}
                            onChange={handleFilterChange}
                            placeholder="Имя персонажа"
                            autoComplete="off"
                        />
                    </label>

                    <label className="admin-logs__filter">
                        <span>Дата от</span>
                        <input
                            type="date"
                            name="dateFrom"
                            className="admin-logs__input admin-logs__input--date"
                            value={filters.dateFrom}
                            onChange={handleFilterChange}
                        />
                    </label>

                    <label className="admin-logs__filter">
                        <span>Дата до</span>
                        <input
                            type="date"
                            name="dateTo"
                            className="admin-logs__input admin-logs__input--date"
                            value={filters.dateTo}
                            onChange={handleFilterChange}
                        />
                    </label>
                </div>

                <div className="admin-logs__filter-actions">
                    <button
                        type="button"
                        className="admin-logs__reset-btn"
                        onClick={handleResetFilters}
                    >
                        Сбросить
                    </button>
                    <button
                        type="submit"
                        className="ucp-simple-yellow-button admin-logs__apply-btn"
                    >
                        Применить
                    </button>
                </div>
            </form>

            {error && <p className="admin-logs__error">{error}</p>}

            <div className="admin-table admin-logs__table">
                <table>
                    <colgroup>
                        {columns.map((column) => (
                            <col
                                key={column.id}
                                className={colClass(column.width) || undefined}
                            />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.id}
                                    className={colClass(column.width)}
                                >
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && items.length === 0 ? (
                            <tr className="admin-table__empty">
                                <td colSpan={columns.length}>Загрузка…</td>
                            </tr>
                        ) : items.length === 0 ? (
                            <tr className="admin-table__empty">
                                <td colSpan={columns.length}>
                                    Записей не найдено
                                </td>
                            </tr>
                        ) : (
                            items.map(renderRow)
                        )}
                    </tbody>
                </table>
            </div>

            {totalItems > 0 && (
                <nav
                    className="admin-logs__pagination"
                    aria-label="Навигация по страницам"
                >
                    <button
                        type="button"
                        className="admin-logs__page-btn"
                        disabled={page <= 1 || loading}
                        onClick={() => goToPage(page - 1)}
                    >
                        Назад
                    </button>

                    {pageNumbers.map((pageNum) => (
                        <button
                            key={pageNum}
                            type="button"
                            className={`admin-logs__page-btn ${pageNum === page ? "admin-logs__page-btn--active" : ""}`}
                            disabled={loading}
                            onClick={() => goToPage(pageNum)}
                        >
                            {pageNum}
                        </button>
                    ))}

                    <button
                        type="button"
                        className="admin-logs__page-btn"
                        disabled={page >= totalPages || loading}
                        onClick={() => goToPage(page + 1)}
                    >
                        Вперёд
                    </button>

                    <form
                        className="admin-logs__page-jump"
                        onSubmit={handlePageInputSubmit}
                    >
                        <label className="admin-logs__page-jump-label">
                            <input
                                type="number"
                                className="admin-logs__page-input"
                                min={1}
                                max={totalPages}
                                value={pageInput}
                                onChange={handlePageInputChange}
                                disabled={loading}
                                aria-label="Номер страницы"
                            />
                        </label>
                        <span className="admin-logs__page-jump-sep">из {totalPages}</span>
                        <button
                            type="submit"
                            className="admin-logs__page-btn admin-logs__page-btn--jump"
                            disabled={loading}
                        >
                            Перейти
                        </button>
                    </form>

                    <span className="admin-logs__pagination-info">
                        {totalItems} записей · стр. {page} из {totalPages}
                    </span>
                </nav>
            )}
        </div>
    );
};

export default AdminLogsPage;
